// src/components/Admin/UsersTab.js
import { useState, useEffect, useCallback } from "react";
import { UserCheck, Ban, Search } from "lucide-react";
import { fetchUsers, setUserStatus } from "../services/adminUsers";

const ROLE_FILTERS = [
  { id: "all", label: "All" },
  { id: "customer", label: "Customers" },
  { id: "tasker", label: "Providers" },
  { id: "admin", label: "Admins" },
];

function formatDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString();
}

function StatusBadge({ status }) {
  const s = status || "active";
  const styles =
    s === "suspended"
      ? "bg-red-100 text-red-700"
      : s === "pending"
      ? "bg-orange-100 text-orange-700"
      : "bg-green-100 text-green-700";
  return (
    <span className={`text-xs px-2.5 py-0.5 rounded-full capitalize ${styles}`}>
      {s}
    </span>
  );
}

export default function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUsers(await fetchUsers());
    } catch (err) {
      console.error("fetchUsers error:", err);
      setError("Could not load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (uid, status) => {
    setBusyId(uid);
    try {
      await setUserStatus(uid, status);
      setUsers((prev) =>
        prev.map((u) => (u.id === uid ? { ...u, status } : u))
      );
    } catch (err) {
      console.error("setUserStatus error:", err);
    } finally {
      setBusyId(null);
    }
  };

  const shown = users.filter((u) => {
    const roleOk = roleFilter === "all" || u.role === roleFilter;
    const s = search.trim().toLowerCase();
    const searchOk =
      !s ||
      (u.email || "").toLowerCase().includes(s) ||
      (u.id || "").toLowerCase().includes(s);
    return roleOk && searchOk;
  });

  return (
    <div>
      {/* Controls */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
          {ROLE_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setRoleFilter(f.id)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                roleFilter === f.id
                  ? "bg-[#006C35] text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="relative">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email or ID"
            className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35]"
          />
        </div>
      </div>

      {loading && <div className="text-gray-500 py-8">Loading users...</div>}
      {error && <div className="text-red-600 py-8">{error}</div>}

      {!loading && !error && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">User</th>
                  <th className="text-left px-4 py-3 font-medium">Role</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-left px-4 py-3 font-medium">Joined</th>
                  <th className="text-right px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {shown.map((u) => {
                  const suspended = (u.status || "active") === "suspended";
                  return (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-gray-800">{u.email || "—"}</div>
                        <div className="text-xs text-gray-400">{u.id}</div>
                      </td>
                      <td className="px-4 py-3 capitalize text-gray-700">
                        {u.role || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={u.status} />
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {formatDate(u.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          {suspended ? (
                            <button
                              onClick={() => changeStatus(u.id, "active")}
                              disabled={busyId === u.id}
                              className="inline-flex items-center gap-1.5 text-green-700 hover:bg-green-50 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
                            >
                              <UserCheck size={16} />
                              Activate
                            </button>
                          ) : (
                            <button
                              onClick={() => changeStatus(u.id, "suspended")}
                              disabled={busyId === u.id}
                              className="inline-flex items-center gap-1.5 text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
                            >
                              <Ban size={16} />
                              Suspend
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {shown.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}